import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { useDispatch } from 'react-redux'

import ENV_VARS from '@/config'
import { breakpoints, colors } from '@/styles'
import useLanguage from '@/hooks/use-language'
import useRole from '@/hooks/use-role'
import { Button } from '@/components/button'
import { DownloadAltIcon, WarningIconTriangle } from '@/components/icons'
import { showWarningModalWithCloseButton } from '@/components/warning-modal'

const { DASHBOARD_URL } = ENV_VARS

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.p`
  font-family: Inter, sans-serif;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: ${colors.black};
  text-align: center;
  margin: 0 0 16px 0;
`
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  > :first-child {
    margin-right: 24px;
  }
`
const customButtonStyle = css`
  width: fit-content;
  align-self: center;
`

const StyledLink = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;

  width: 128px;
  margin: 0;
  padding: 16px;
  color: ${colors.blue16};
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: center;
  text-decoration: none;
  min-width: unset;

  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  width: fit-content;
  margin: 0;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    align-self: center;
    width: 48%;
    margin: 12px 0;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    width: unset;
  }

  @media (max-width: ${breakpoints.smallPhone}) {
    width: 48%;
  }
`
const DownloadIconCustom = styled(DownloadAltIcon)`
  width: 22px;
  height: 22px;
  margin-left: 9px;
`

const DownloadSample = () => {
  const { t } = useTranslation('landlords')
  const { language } = useLanguage()
  const role = useRole()
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()
  const router = useRouter()
  const isLandlord = role === 'landlord'
  const isTenant = role === 'tenant'

  const pdfHref =
    language.value === 'zh'
      ? '/pdfs/sample-livrent-report-cn.pdf'
      : '/pdfs/sample-livrent-report.pdf'

  const pdfName =
    language.value === 'zh'
      ? 'sample-livrent-report-cn.pdf'
      : 'sample-livrent-report.pdf'

  const handleClickPostAListing = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    if (isTenant) {
      showWarningModalWithCloseButton({
        title: t(
          'r.landlord.view.logged_in_as_renter.title',
          "Oops! It looks like you're logged in as a renter."
        ),
        message: t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        ),
        icon: WarningIconTriangle,
        iconBgColor: '#FCF5D7',
        button: {
          text: t('common.log_out', 'Log Out'),
          onClick: logout
        }
      })
    } else {
      isLandlord
        ? (window.location.href = `${DASHBOARD_URL}/listing/create`)
        : router.push({
            pathname: '/post-a-listing',
            query: { accountType: 'essentials' }
          })
    }
  }

  return (
    <Wrapper>
      <Title>
        {t(`landlords:Don't take your chances with the wrong tenants`)}
      </Title>
      <ButtonWrapper>
        <Button
          id="landlord-report-get-started-click"
          customStyle={customButtonStyle}
          theme="primary"
          onClick={handleClickPostAListing}
        >
          {t('landlords:Get Started')}
        </Button>
        <StyledLink
          id="landlord-download-sample-report-click"
          href={pdfHref}
          download={pdfName}
        >
          {t('landlords:Download Sample PDF')}
          <DownloadIconCustom />
        </StyledLink>
      </ButtonWrapper>
    </Wrapper>
  )
}

export default DownloadSample
